/**
 * A seção *Retornos da autorização* do detalhe do Caso (`TP-056`, T6).
 *
 * Mostra o segundo relógio do Caso, a vez da Anery, ao lado da espera da operadora (que fica em *Esperas* e continua
 * contando), e os retornos com o que cada um propõe. Nada transita sozinho: confirmar é um clique, e o botão diz a
 * hora que vai ser gravada — a da chegada do e-mail, não a do clique.
 */

import Button from '@mui/material/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { formatarDataHora } from '../../shared/dominio/datas';
import { chavesDeCasos } from './api/casos';
import { formatarDuracaoEmHorasEMinutos } from './api/tipos';
import {
  chavesDeRetornos,
  confirmarRetorno,
  descartarRetorno,
  marcarQuestionamentoRespondido,
  opcoesDosRetornosDoCaso,
} from '../../shared/retornos/api';
import type { MotivoDoDescarte, RetornoDoCaso, RetornosDoCaso as Retornos } from '../../shared/retornos/tipos';
import {
  MOTIVOS_PARA_ESCOLHA,
  ROTULOS_DA_PROPOSTA,
  ROTULOS_DO_MOTIVO,
  ROTULOS_DO_RESULTADO,
  mensagemDeErroDoRetorno,
  rotuloDaOrigem,
  textoDaConfirmacao,
} from '../../shared/retornos/vocabulario';

function textoDaDecisao(retorno: RetornoDoCaso): string {
  if (retorno.decisao === 'Confirmed') {
    return `confirmado em ${retorno.decididoEm === null ? '—' : formatarDataHora(retorno.decididoEm)}`;
  }
  if (retorno.decisao === 'Discarded') {
    return `descartado: ${retorno.motivoDoDescarte === null ? 'sem motivo' : ROTULOS_DO_MOTIVO[retorno.motivoDoDescarte]}`;
  }
  return 'a confirmar';
}

export function RetornosDoCaso({ casoId }: { casoId: string }): React.JSX.Element {
  const consulta = useQuery(opcoesDosRetornosDoCaso(casoId));
  const clienteDeConsultas = useQueryClient();
  const [motivos, definirMotivos] = useState<Record<string, MotivoDoDescarte>>({});

  const aoAtualizar = (atualizado: Retornos): void => {
    clienteDeConsultas.setQueryData(chavesDeRetornos.doCaso(casoId), atualizado);
    // Confirmar pode transitar o Caso: o detalhe, a linha do tempo, as listas e a CI diária mudam junto.
    void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.detalhe(casoId) });
    void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.linhaDoTempo(casoId) });
    void clienteDeConsultas.invalidateQueries({ queryKey: [...chavesDeCasos.raiz, 'lista'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: [...chavesDeRetornos.raiz, 'lista'] });
  };

  const confirmar = useMutation({
    mutationFn: (emailId: string) => confirmarRetorno(casoId, emailId),
    onSuccess: aoAtualizar,
  });
  const descartar = useMutation({
    mutationFn: ({ emailId, motivo }: { emailId: string; motivo: MotivoDoDescarte }) =>
      descartarRetorno(casoId, emailId, motivo),
    onSuccess: aoAtualizar,
  });
  const responder = useMutation({
    mutationFn: (emailId: string) => marcarQuestionamentoRespondido(casoId, emailId),
    onSuccess: aoAtualizar,
  });

  const ocupado = confirmar.isPending || descartar.isPending || responder.isPending;
  const erro = confirmar.error ?? descartar.error ?? responder.error;
  const limparErros = (): void => {
    confirmar.reset();
    descartar.reset();
    responder.reset();
  };

  if (consulta.isPending) return <p className="text-[0.8125rem] text-[var(--tinta-2)]">Carregando os retornos…</p>;
  if (consulta.isError) {
    return (
      <p role="alert" className="flex flex-wrap items-center gap-2 text-[0.8125rem] text-[var(--roxo)]">
        Não foi possível carregar os retornos da autorização.
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            void consulta.refetch();
          }}
        >
          Tentar de novo
        </Button>
      </p>
    );
  }

  const { vezDaAnery, retornos } = consulta.data;
  // "Anery respondeu" só vale para o questionamento que abriu a vez em curso: o mais recente confirmado, e só
  // enquanto a vez está aberta. O retorno seguinte da operadora fecha a vez sem preencher `respondidoEm`.
  const questionamentoDaVez =
    vezDaAnery?.fim !== null
      ? null
      : (retornos
          .filter((r) => r.resultado === 'PendingDocuments' && r.decisao === 'Confirmed' && r.respondidoEm === null)
          .reduce<RetornoDoCaso | null>(
            (maisRecente, r) => (maisRecente === null || r.recebidoEm > maisRecente.recebidoEm ? r : maisRecente),
            null,
          )?.emailId ?? null);

  return (
    <div className="space-y-3">
      <dl className="anery-ficha-caso">
        <dt>Vez da Anery</dt>
        <dd>
          {vezDaAnery === null ? (
            'Nenhuma pendência confirmada.'
          ) : (
            <>
              {formatarDuracaoEmHorasEMinutos(vezDaAnery.duracaoMinutos * 60)} · desde{' '}
              {formatarDataHora(vezDaAnery.inicio)}
              {' até '}
              {vezDaAnery.fim === null ? 'agora (aberta)' : formatarDataHora(vezDaAnery.fim)}
              <span className="block text-xs text-[var(--tinta-2)]">
                A espera de terceiro (em Esperas) continua contando ao lado.
              </span>
            </>
          )}
        </dd>
      </dl>

      {retornos.length === 0 ? (
        <p className="text-[0.8125rem] text-[var(--tinta-2)]">
          Nenhum retorno da autorização com resultado marcado. O resultado se marca na triagem, olhando a carta.
        </p>
      ) : (
        <ul className="list-none pl-0" aria-label="Retornos da autorização">
          {retornos.map((retorno) => {
            const motivo = motivos[retorno.emailId] ?? 'WrongReading';
            const vezAberta = retorno.emailId === questionamentoDaVez;
            return (
              <li key={retorno.emailId} className="anery-item flex-wrap gap-2">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{ROTULOS_DO_RESULTADO[retorno.resultado]}</span>
                  <span className="block text-xs text-[var(--tinta-2)]">
                    {rotuloDaOrigem(retorno.origem, retorno.confianca)} · chegou {formatarDataHora(retorno.recebidoEm)}
                    {' · '}
                    {textoDaDecisao(retorno)}
                    {retorno.respondidoEm === null
                      ? null
                      : ` · respondido em ${formatarDataHora(retorno.respondidoEm)}`}
                  </span>
                  {retorno.proposta === null ? null : (
                    <span className="block text-xs">Propõe: {ROTULOS_DA_PROPOSTA[retorno.proposta]}</span>
                  )}
                </span>

                {retorno.decisao === null && retorno.proposta !== null ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <Button
                      size="small"
                      variant="contained"
                      disabled={ocupado}
                      onClick={() => {
                        limparErros();
                        confirmar.mutate(retorno.emailId);
                      }}
                    >
                      {textoDaConfirmacao(retorno.proposta, retorno.recebidoEm, retorno.efetivoEm)}
                    </Button>
                    <label className="text-xs">
                      <span className="sr-only">Motivo do descarte</span>
                      <select
                        aria-label={`Motivo do descarte do retorno de ${formatarDataHora(retorno.recebidoEm)}`}
                        value={motivo}
                        onChange={(evento) => {
                          definirMotivos((atual) => ({
                            ...atual,
                            [retorno.emailId]: evento.target.value as MotivoDoDescarte,
                          }));
                        }}
                      >
                        {MOTIVOS_PARA_ESCOLHA.map((m) => (
                          <option key={m} value={m}>
                            {ROTULOS_DO_MOTIVO[m]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={ocupado}
                      onClick={() => {
                        limparErros();
                        descartar.mutate({ emailId: retorno.emailId, motivo });
                      }}
                    >
                      Descartar
                    </Button>
                  </span>
                ) : null}

                {vezAberta ? (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={ocupado}
                    onClick={() => {
                      limparErros();
                      responder.mutate(retorno.emailId);
                    }}
                  >
                    Anery respondeu
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {erro ? (
        <p role="alert" aria-live="polite" className="text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDeErroDoRetorno(erro)}
        </p>
      ) : null}
    </div>
  );
}
